import React from "react";
import { DECISION_INPUT_OPTIONS } from "@/api/decision-input-values";
import { Button, FormField, Input, Select } from "@/ds";
import { useT } from "../../i18n";

export function hasTypedInputEditor(field: string): boolean {
  return (DECISION_INPUT_OPTIONS.listFields as readonly string[]).includes(field)
    || ["catering.budget", "logistics.delivery_window", "customer.type", "catering.drinks_from_neo", "catering.coffee_setup", "event.venue_name", "event.event_address"].includes(field);
}

export function initialTypedInput(field: string, value: unknown): unknown {
  if (field === "customer.type") return (DECISION_INPUT_OPTIONS.customerTypes as readonly unknown[]).includes(value) ? value : null;
  if ((DECISION_INPUT_OPTIONS.listFields as readonly string[]).includes(field)) return Array.isArray(value) ? [...value] : [""];
  if (value && typeof value === "object" && !Array.isArray(value)) return { ...value };
  if (field === "catering.budget") return { version: 1, currency: "EUR", basis: "total", stated_tax_basis: "unspecified", kind: "exact", amount_minor: null, upper_minor: null, confirmed: false };
  if (field === "logistics.delivery_window") return { version: 1, date: "", start: "", end: "", timezone: "Europe/Berlin" };
  if (field === "catering.drinks_from_neo" || field === "catering.coffee_setup") return { version: 1, need: "", supplier: "", details: [] };
  if (field === "event.venue_name") return { version: 1, kind: "", name: "" };
  return { version: 1, street: null, house_number: null, postal_code: null, locality: null };
}

/** UI shape checks only; the server revalidates the complete typed value and authority. */
export function typedInputCanSave(field: string, value: unknown): boolean {
  if (field === "customer.type") return value === null || (DECISION_INPUT_OPTIONS.customerTypes as readonly unknown[]).includes(value);
  const list = (input: unknown, max: number) => Array.isArray(input) && input.length <= max
    && input.every((item) => typeof item === "string" && item.trim() && item.length <= 500);
  if ((DECISION_INPUT_OPTIONS.listFields as readonly string[]).includes(field)) return list(value, 32);
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (field === "catering.budget") return Number.isSafeInteger(v.amount_minor) && Number(v.amount_minor) >= 0
    && (v.kind === "range" ? Number.isSafeInteger(v.upper_minor) && Number(v.upper_minor) >= Number(v.amount_minor) : v.upper_minor === null);
  if (field === "logistics.delivery_window") return /^\d{4}-\d{2}-\d{2}$/.test(String(v.date))
    && [v.start, v.end].every(x => /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/.test(String(x)));
  if (field === "catering.drinks_from_neo" || field === "catering.coffee_setup") {
    return (DECISION_INPUT_OPTIONS.drinkNeeds as readonly unknown[]).includes(v.need)
      && (DECISION_INPUT_OPTIONS.drinkSuppliers as readonly unknown[]).includes(v.supplier)
      && list(v.details, 24) && (v.details as string[]).every((item) => item.length <= 160)
      && (v.need !== "not_required" || v.supplier === "unspecified" && (v.details as unknown[]).length === 0)
      && ((v.details as unknown[]).length === 0 || v.need === "required")
      && new Set(v.details as unknown[]).size === (v.details as unknown[]).length;
  }
  if (field === "event.venue_name") return (DECISION_INPUT_OPTIONS.venueKinds as readonly unknown[]).includes(v.kind)
    && typeof v.name === "string" && v.name.trim().length > 0 && v.name.length <= 160;
  const parts = [v.street, v.house_number, v.postal_code, v.locality];
  return parts.some((part) => typeof part === "string" && part.trim())
    && parts.every((part) => part === null || typeof part === "string" && part.trim() && part.length <= 160)
    && [v.street, v.locality].every((part) => part === null || /\p{L}/u.test(String(part)))
    && (v.house_number === null || /^[0-9]{1,4}(?:\s*[a-zA-Z])?(?:\s*[-/]\s*[0-9]{1,4}(?:\s*[a-zA-Z])?)?$/.test(String(v.house_number)))
    && (v.postal_code === null || /^[0-9]{5}$/.test(String(v.postal_code)));
}

function ListEditor({ value, onChange, id }: { value: string[]; onChange: (value: string[]) => void; id: string }) {
  const t = useT();
  return <div id={id} className="stack-sm">
    {value.length === 0 && <span>{t("ws.input.noItems")}</span>}
    {value.map((item, index) => <div key={index} className="row" style={{ gap: "var(--space-2)" }}>
      <Input size="sm" value={item} aria-label={t("ws.input.item", { n: index + 1 })}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(value.map((prior, i) => i === index ? event.target.value : prior))} />
      <Button size="sm" variant="ghost" aria-label={t("ws.input.removeItem", { n: index + 1 })}
        onClick={() => onChange(value.filter((_, i) => i !== index))}>{t("common.remove")}</Button>
    </div>)}
    <Button size="sm" variant="ghost" onClick={() => onChange([...value, ""])}>{t("ws.input.addItem")}</Button>
  </div>;
}

export function TypedFactEditor({ field, value, onChange, id }: {
  field: string; value: unknown; onChange: (value: unknown) => void; id: string;
}) {
  const t = useT();
  if (field === "customer.type") return <Select id={id} value={String(value ?? "")}
    options={[{ value: "", label: t("common.noInfo") }, ...DECISION_INPUT_OPTIONS.customerTypes.map((type) => ({ value: type, label: t(`factValue.${type}`) }))]}
    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value || null)} />;
  if (Array.isArray(value)) return <ListEditor id={id} value={value} onChange={onChange} />;
  const v = (value ?? {}) as Record<string, unknown>;
  const patch = (key: string, next: unknown) => onChange({ ...v, [key]: next });
  const select = (key: string, options: readonly string[]) => <FormField label={t(`ws.input.${key}`)} htmlFor={`${id}-${key}`}>
    <Select id={`${id}-${key}`} value={String(v[key] ?? "")}
      options={[{ value: "", label: t("common.noInfo") }, ...options.map((option) => ({ value: option, label: t(`ws.input.${key}.${option}`) }))]}
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
        const next = event.target.value;
        if (key === "need" && next === "not_required") onChange({ ...v, need: next, supplier: "unspecified", details: [] });
        else if (key === "need" && next !== "required") onChange({ ...v, need: next, details: [] });
        else patch(key, next);
      }} />
  </FormField>;
  const input = (key: string) => <FormField label={t(`ws.input.${key}`)} htmlFor={`${id}-${key}`}>
    <Input id={`${id}-${key}`} size="sm" value={String(v[key] ?? "")}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => patch(key, event.target.value || (field === "event.event_address" ? null : ""))} />
  </FormField>;
  if (field === "catering.budget") return <div id={id} className="stack-sm">
    {([['basis', ['total', 'per_person'], 'budgetBasis'], ['stated_tax_basis', ['net', 'gross', 'unspecified'], 'tax'],
      ['kind', ['exact', 'approximate', 'range', 'lower_bound', 'upper_bound'], 'budgetKind']] as const).map(([key, options, prefix]) =>
      <FormField key={key} label={t(`workflow.budget.${key === "stated_tax_basis" ? "tax" : key}`)}><Select value={String(v[key])}
        options={options.map(option => ({ value: option, label: t(`workflow.${prefix}.${option}`) }))}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...v, [key]: event.target.value,
          ...(key === "kind" && event.target.value !== "range" ? { upper_minor: null } : {}) })} /></FormField>)}
    {(['amount_minor', ...(v.kind === "range" ? ['upper_minor'] : [])]).map(key => <FormField key={key} label={t(`workflow.budget.${key === "amount_minor" ? "amount" : "upper"}`)}>
      <Input type="number" min="0" step="0.01" value={v[key] == null ? "" : Number(v[key]) / 100}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => patch(key, /^\d+(?:\.\d{1,2})?$/.test(event.target.value)
          ? Number(event.target.value.split('.')[0]) * 100 + Number((event.target.value.split('.')[1] ?? '').padEnd(2, '0')) : null)} /></FormField>)}
    <label><input type="checkbox" checked={v.confirmed === true} onChange={e => patch("confirmed", e.target.checked)} /> {t("workflow.budget.confirmed")}</label>
  </div>;
  if (field === "logistics.delivery_window") return <div id={id} className="stack-sm">
    {(['date', 'start', 'end'] as const).map(key => <FormField key={key} label={t(`workflow.window.${key}`)}>
      <Input type={key === "date" ? "date" : "time"} value={String(v[key] ?? "")}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => patch(key, event.target.value)} /></FormField>)}
  </div>;
  return <div id={id} className="stack-sm">
    {(field === "catering.drinks_from_neo" || field === "catering.coffee_setup") ? <>
      {select("need", DECISION_INPUT_OPTIONS.drinkNeeds)}
      {v.need !== "not_required" && select("supplier", DECISION_INPUT_OPTIONS.drinkSuppliers)}
      {v.need === "required" && <FormField label={t("ws.input.details")}>
        <ListEditor id={`${id}-details`} value={Array.isArray(v.details) ? v.details : []} onChange={(next) => patch("details", next)} />
      </FormField>}
    </> : field === "event.venue_name" ? <>
      {select("kind", DECISION_INPUT_OPTIONS.venueKinds)}
      {input("name")}
    </> : <>{input("street")}{input("house_number")}{input("postal_code")}{input("locality")}</>}
  </div>;
}
